import { useCallback, useMemo } from 'react';

import { useAuthentication } from '@givebacks/api';
import { useFeatureFlag_Core } from 'core';
import { useShowCauseW_Selector } from 'core/Api';
import { useUrlSearchParams } from 'core/Hooks';
import { useFeatureValue } from 'core/Packages';
import { useNavigate } from 'react-router-dom';
import { useShowPostCheckoutCart } from 'store/Api';
import { ButtonMantine, Card, ImageMantine, MembershipCard, MembershipDetails, Stack } from 'ui';

import { useNavigateToAuth } from '../../../hooks/useNavigateToAuth/useNavigateToAuth';
import { PostCheckoutRedirectPageTemplate } from '../PostCheckoutRedirectPageTemplate/PostCheckoutRedirectPageTemplate';
import { POST_CHECKOUT_IMAGE_URLS } from '../postCheckoutImageUrls';

type MembershipCardWithDetailsProps = {
  /**
   * If true, membership details will be hidden
   */
  isRedacted?: boolean;
};

const MembershipCardWithDetails = ({ isRedacted = false }: MembershipCardWithDetailsProps) => {
  const { cartId, email } = useUrlSearchParams(['cartId', 'email']);

  const { data: checkoutCartData } = useShowPostCheckoutCart({
    cartId,
    email,
  });

  const { data: logo } = useShowCauseW_Selector({
    uuid: checkoutCartData?.cart.cause.uuid,
    select: (cause) => cause?.logo?.url,
  });

  const cart = checkoutCartData?.cart;
  const cause = { name: cart?.cause.name ?? 'Your Cause', logo };
  const card =
    cart?.membership_card?.front_url && cart?.membership_card?.back_url
      ? {
          front_url: cart?.membership_card.front_url,
          back_url: cart?.membership_card.back_url,
        }
      : undefined;

  return (
    <Card p="lg" shadow="md">
      <Stack gap="lg">
        {isRedacted ? (
          <ImageMantine src={POST_CHECKOUT_IMAGE_URLS.emptyPta} />
        ) : (
          <MembershipCard
            direction="column"
            membership={{
              first_name: cart?.membership_card?.first_name ?? '',
              last_name: cart?.membership_card?.last_name ?? '',
              expires_at: cart?.membership_card?.expires_at ?? '',
              cause,
              card,
            }}
          />
        )}

        <MembershipDetails
          isRedacted={isRedacted}
          membership={{
            first_name: cart?.membership_card?.first_name ?? '',
            last_name: cart?.membership_card?.last_name ?? '',
            expires_at: cart?.membership_card?.expires_at ?? '',
            cause,
          }}
        />
      </Stack>
    </Card>
  );
};

interface MembershipRedirectPageProps {
  isStg: boolean;
}

const MembershipRedirectPage = ({ isStg }: MembershipRedirectPageProps) => {
  const { handleNavigateToAuth } = useNavigateToAuth();
  const { isAuthenticated } = useAuthentication();
  const navigate = useNavigate();
  const { isFeatureEnabled: isAccountPageEnabled } = useFeatureFlag_Core({
    featureId: 'user.account_page',
  });

  const { cartId, email, firstName, lastName } = useUrlSearchParams([
    'cartId',
    'email',
    'firstName',
    'lastName',
  ]);
  const { data } = useShowPostCheckoutCart({
    cartId,
    email,
  });

  const causeName = data?.cart.cause.name ?? 'your cause';

  const featureFlag = isStg ? 'ic_membership_with_stg' : 'ic_membership_without_stg';

  const testVariation = useFeatureValue<'A' | 'B'>(featureFlag, 'A');
  const walletRoute = isAccountPageEnabled ? '/account' : '/wallet';

  const variantPropMap = useMemo(
    () =>
      new Map([
        [
          'A',
          {
            rightSection: <ImageMantine src={POST_CHECKOUT_IMAGE_URLS.membership} />,
          },
        ],
        [
          'B',
          {
            rightSection: <MembershipCardWithDetails />,
          },
        ],
        [
          'C',
          {
            rightSection: <MembershipCardWithDetails isRedacted />,
          },
        ],
      ]),
    []
  );

  const onPrimaryCtaClick = useCallback(() => {
    if (isAuthenticated) {
      navigate(walletRoute);
      return;
    }

    handleNavigateToAuth({
      page: 'sign-up',
      redirect: walletRoute,
      additionalParams: {
        firstName,
        lastName,
        email,
      },
    });
  }, [isAuthenticated, handleNavigateToAuth, firstName, lastName, email, navigate, walletRoute]);

  return (
    <PostCheckoutRedirectPageTemplate
      title="Access your membership with the Givebacks app"
      leftSection={
        <PostCheckoutRedirectPageTemplate.AppBenefits>
          <PostCheckoutRedirectPageTemplate.AppBenefit>
            Access your membership any time
          </PostCheckoutRedirectPageTemplate.AppBenefit>
          <PostCheckoutRedirectPageTemplate.AppBenefit>
            Manage donations and purchases for all your organizations
          </PostCheckoutRedirectPageTemplate.AppBenefit>
          <PostCheckoutRedirectPageTemplate.AppBenefit>
            {isStg
              ? `View 15,000 online merchants and 10,000 local restaurants supporting ${causeName}`
              : `View receipts and payment history`}
          </PostCheckoutRedirectPageTemplate.AppBenefit>
        </PostCheckoutRedirectPageTemplate.AppBenefits>
      }
      rightSection={variantPropMap.get(testVariation)?.rightSection}
      cta={<ButtonMantine onClick={onPrimaryCtaClick}>View Membership Card</ButtonMantine>}
    />
  );
};

// Wrapper handles making sure isStg is calculated correctly before rendering the page,
// so that growthbook can choose the variant
const WrappedMembershipRedirectPage = () => {
  const { cartId, email } = useUrlSearchParams(['cartId', 'email', 'firstName', 'lastName']);

  const { data } = useShowPostCheckoutCart({
    cartId,
    email,
  });

  if (!data) {
    return null;
  }

  const isStg = data?.cart.enrolled_in_shop_to_give;

  return <MembershipRedirectPage isStg={isStg} />;
};

export { MembershipCardWithDetails, WrappedMembershipRedirectPage as MembershipRedirectPage };
